import { FormHeading, FormSwitch } from "components/form-fields";
import React, { useEffect, useMemo, useState } from "react";
import { ShowHide } from "components/layout";
import { Table } from "components/listing";
import { UserHasAccess } from "components/user-access";
import { AccountService } from "services/AccountService";
import { MfaService } from "services";
import { AlertUnsavedData, NotificationInline, NotificationToaster } from "components/notifications";
import { Intent } from "@blueprintjs/core";
import { StatusBlock } from "components/status";
import { useValidation } from "hooks/useValidation/useValidation";
import { useDirtyData } from "hooks/useDirtyData";
import { ButtonSave } from "components/buttons/ButtonSave";

export function AccountMultifactorAuthentication() {
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    const defaultMfaSettings = {
        mfaEnabled: false,
        mfaEnforced: false,
        allowedMfaTypes: []
    };

    const [originalMfaSettings, setOriginalMfaSettings] = useState({
        ...defaultMfaSettings
    });

    const [accountMfaSettings, setAccountMfaSettings] = useState({
        ...defaultMfaSettings
    });

    const [mfaTypes, setMfaTypes] = useState([]);

    const [ssoIsEnabled, setSsoIsEnabled] = useState(false);
    const accountCanSetupSso = UserHasAccess("SSO:Manage");
    
    const mfaTypesHeaderValues = ["Title", "Security Level", "Description", ""];
    const mfaTypesHeaders = {
        headers: mfaTypesHeaderValues.map((h) => ({ key: h, value: h }))
    };

    const mfaEnabled = !ssoIsEnabled && accountMfaSettings.mfaEnabled;

    const [isValid, errors, validate] = useValidation();

    const isDataDirty = useDirtyData(originalMfaSettings, accountMfaSettings);

    useEffect(() => {
        const rules = [];

        if (mfaEnabled) {
            rules.push({ fieldName: "allowedMfaTypes", required: true });
        }

        validate(rules, accountMfaSettings);
    }, [accountMfaSettings, mfaEnabled]);

    const onSave = () => {
        setSaving(true);

        const settingsToSave = {
            ...accountMfaSettings
        };

        if (!accountMfaSettings.mfaEnabled) {
            // If MFA is being disabled, we want to clear out the allowed MFA types since they won't be relevant anymore
            settingsToSave.allowedMfaTypes = [];
            settingsToSave.mfaEnforced = false;
        }

        MfaService.saveAccountMfaSettings(settingsToSave).then(
            () => {
                setOriginalMfaSettings({ ...settingsToSave });
                setSaving(false);
                NotificationToaster.show(Intent.SUCCESS, "MFA settings updated successfully.");
            },
            () => {
                setSaving(false);
                NotificationToaster.show(Intent.DANGER, "An error occurred while saving MFA settings. Please try again.");
            }
        )
    }

    const loadSsoConfig = () => {
        if (accountCanSetupSso) {
            return AccountService.getSsoConfig().then(
                (config) => {
                    setSsoIsEnabled(config.ssoEnabled);
                }
            )
        }

        return Promise.resolve();
    }

    useEffect(() => {
        Promise.all([
            MfaService.getMfaTypes(),
            MfaService.getAccountMfaSettings(),
            loadSsoConfig()
        ]).then(([mfaTypesResponse, accountMfaSettingsResponse]) => {
            setAccountMfaSettings(accountMfaSettingsResponse);
            setOriginalMfaSettings({...accountMfaSettingsResponse});
            setMfaTypes(mfaTypesResponse.mfaTypes);
            setLoading(false);
        });
    }, []);

    const onEnableMfaChange = () => {
        setAccountMfaSettings(prev => {
            return {
                ...prev,
                mfaEnabled: !prev.mfaEnabled
            }
        });
    }

    const onEnforceMfaChange = () => {
        setAccountMfaSettings(prev => {
            return {
                ...prev,
                mfaEnforced: !prev.mfaEnforced
            }
        });
    }

    const getDescriptionForMfaType = (type) => {
        switch (type) {
            case "TOTP":
                return "Time-based One Time Passwords (TOTP) are generated by an app on the user's phone, such as Google Authenticator or Microsoft Authenticator. When the user logs in, they will be required to enter the current code from their authenticator app.";
            case "SMS":
                return "SMS allows users to receive a one-time code via text message to their registered phone number. When the user logs in, they will be required to enter the code sent to their phone.";
            case "Email":
                return "Email allows users to receive a one-time code via email to their registered email address. When the user logs in, they will be required to enter the code sent to their email. Note that this method is less secure than other MFA types, as email accounts can be compromised.";
            default:
                return "";
        }
    }

    const getSecurityLevelForMfaType = (type) => {
        switch (type) {
            case "TOTP":
                return <StatusBlock status={"green"}>Strong</StatusBlock>;
            case "SMS":
                return <StatusBlock status={"red"}>Weak</StatusBlock>;
            default:
                return <StatusBlock status={"red"}>Very weak</StatusBlock>;
        }
    }

    function onMfaTypeSwitchChange(enabled, mfaType) {
        setAccountMfaSettings(prev => {
            let allowedMfaTypes = [...prev.allowedMfaTypes];
            if (enabled) {
                allowedMfaTypes.push(mfaType);
            } else {
                allowedMfaTypes = allowedMfaTypes.filter(t => t !== mfaType);
            }
            return {
                ...prev,
                allowedMfaTypes
            }
        });
    }

    const multifactorAuthenticationTypeRows = mfaTypes.map(t => ({
        cells: [
            t.name,
            getSecurityLevelForMfaType(t.key),
            getDescriptionForMfaType(t.key),
            <FormSwitch
                onChange={(s) => { onMfaTypeSwitchChange(s.currentTarget.checked, t.key) }}
                checked={accountMfaSettings.allowedMfaTypes.includes(t.key)}
                loading={loading}
                disabled={saving}
                key={`mfa-type-${t.key}`}
                id={`mfa-type-switch-${t.key}`}
            ></FormSwitch>
        ],
        key: t.key
    }));

    const numberOfMfaTypesBeingDisabled = useMemo(() => {
        if (!accountMfaSettings.allowedMfaTypes || !originalMfaSettings.allowedMfaTypes) {
            return 0;
        };

        return originalMfaSettings.allowedMfaTypes.filter(t => !accountMfaSettings.allowedMfaTypes.includes(t)).length;
    }, [accountMfaSettings, originalMfaSettings]);

    return <div className="row">
        <div className='inline-items spacer-bottom'>
            <h2>Multi-Factor Authentication (MFA)</h2>
        </div>
        <NotificationInline intent={Intent.NONE} show={ssoIsEnabled} text={"MFA cannot be enabled while SSO is active."} />
        <NotificationInline intent={Intent.WARNING} show={numberOfMfaTypesBeingDisabled > 0} text={`You are about to disable ${numberOfMfaTypesBeingDisabled > 1 ? 'multiple' : 'one'} MFA type${numberOfMfaTypesBeingDisabled > 1 ? 's' : ''}. Any users using ${numberOfMfaTypesBeingDisabled > 1 ? 'these types' : 'this type'} will need to setup MFA again.`} />

        <FormSwitch
            disabled={saving || ssoIsEnabled}
            loading={loading}
            headingText="Enable MFA"
            helperText={"When enabling MFA, users will have the option to setup MFA. However, this won't be enforced until you enable the 'Enforce MFA' option below."}
            checked={mfaEnabled}
            onChange={onEnableMfaChange}>
        </FormSwitch>
        <ShowHide
            evaluator={mfaEnabled}
            show={<>
                <FormSwitch
                    disabled={saving || ssoIsEnabled}
                    loading={loading}
                    headingText="Enforce MFA"
                    helperText={"When enforcing MFA, all users will be required to use MFA when logging in. Users who have not yet set up MFA will be prompted to do so when they next log in."}
                    checked={accountMfaSettings.mfaEnforced}
                    onChange={onEnforceMfaChange}>
                </FormSwitch>
                <div className="spacer-bottom">
                    <FormHeading loading={loading} headingLevel={"h4"}>Enabled MFA Types</FormHeading>
                    <Table
                        headerData={mfaTypesHeaders}
                        data={multifactorAuthenticationTypeRows}
                        loadingData={loading}
                        fitToContent={false}
                        dangerHelperText={errors.allowedMfaTypes ? "At least one type must be enabled when MFA is enabled." : ""}
                    />
                </div>
            </>}
        />

        <AlertUnsavedData
            isDirty={isDataDirty && !ssoIsEnabled}>
        </AlertUnsavedData>

        <div className='button-row'>
            <ButtonSave
                onClick={onSave}
                simpleDisabled={!isValid || ssoIsEnabled || saving}
                loading={loading}
                disabled={saving}
            ></ButtonSave>
        </div>
    </div>
}